/**
 * Persistent summary of what is currently filtered, one removable chip each.
 *
 * Sits above the collapsible sections so a URL shared with filters applied
 * still shows what is on when every section is collapsed - the problem it was
 * built for.
 *
 * Existed twice, near-identically, in `MindspaceFilterPanel` and
 * `QuickBytesFilterPanel`. The two differed in exactly one chip and one guard,
 * and both differences are now props:
 *
 * - **`showPausedChip`** - Knowledge Archive offers "Show paused", so it has a
 *   "Show paused" chip to remove. Quick Bytes has no paused toggle, so passing
 *   `false` keeps a chip out of a panel that could never have set it.
 * - **the render guard** - Quick Bytes' local `hasChipToRender` is the shared
 *   implementation now, because it is the superset. `hasAnyFilter` is true for
 *   `bookmarked` too, and Quick Bytes deliberately renders NO chip for that
 *   (its tab switcher already says so) - so keying the strip off `hasAnyFilter`
 *   drew an empty `border-b` bar on the Bookmarks tab. Deriving the guard from
 *   the chips actually rendered makes that unrepresentable rather than fixed.
 *
 * Note the guard folds `showPausedChip` in: a Knowledge Archive user whose only
 * active filter is "Show paused" has exactly one chip, and a guard that ignored
 * the flag would hide the strip out from under it.
 */

import {
  Bookmark,
  BookOpen,
  CalendarCheck,
  Clock,
  PauseCircle,
  Search,
  Tag,
} from 'lucide-react';
import { useSourceLessonOptions } from '@/hooks/mindspace/useMindspaceOptions';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import { SummaryChip } from '@/components/features/mindspace/filters/SummaryChip';
import { WINDOW_LABEL } from '@/components/features/mindspace/filters/windowLabels';

interface ActiveFilterChipsProps {
  /** Render the "Show paused" chip. Knowledge Archive only - Quick Bytes has
   *  no paused toggle to have switched it on. */
  showPausedChip?: boolean;
  /**
   * `'row'` is the phone header's: one line that scrolls sideways under the
   * search box, chips drawn 32px with a 44px tap area, and no chip for the
   * search - the box right above already shows it. `'strip'` (default) is the
   * wrapping strip at the top of a filter panel.
   */
  variant?: 'strip' | 'row';
}

export function ActiveFilterChips({
  showPausedChip = false,
  variant = 'strip',
}: ActiveFilterChipsProps) {
  const row = variant === 'row';
  const {
    q,
    selectedLessons,
    selectedDomain,
    selectedUser,
    window: timeWindow,
    dueToday,
    includeArchived,
    setFilters,
  } = useTagFilters();
  const { data: lessonOptions = [] } = useSourceLessonOptions();

  const pausedChip = showPausedChip && includeArchived;
  const searchChip = !row && q.length > 0;

  // Derived from the chips that would actually render, NOT from
  // `hasAnyFilter` - see the module docstring.
  const hasChipToRender =
    searchChip ||
    selectedLessons.length > 0 ||
    selectedDomain.length > 0 ||
    selectedUser.length > 0 ||
    dueToday ||
    pausedChip ||
    timeWindow !== 'all';
  if (!hasChipToRender) return null;

  const lessonTitle = (id: string) =>
    lessonOptions.find(o => o.id === id)?.title ?? 'Lesson';

  const chip = row ? 'h-8 shrink-0 px-3 tap-target' : undefined;

  return (
    <div
      className={
        row
          ? // Full-bleed so chips scroll out under the screen edge, not a
            // gutter; tall enough that each chip's 44px tap area fits.
            '-mx-4 overflow-x-auto px-4 py-1.5'
          : 'border-b border-border bg-muted/30 px-4 py-2'
      }
    >
      {/* A named group so assistive tech announces the strip as a unit — and
          so a story can assert the WHOLE strip is absent, not just one chip. */}
      <div
        role='group'
        aria-label='Active filters'
        className={row ? 'flex w-max gap-1.5' : 'flex flex-wrap gap-1'}
      >
        {searchChip && (
          <SummaryChip
            className={chip}
            icon={<Search className='h-3 w-3' />}
            label={`"${q}"`}
            onRemove={() => setFilters({ q: '' })}
            ariaLabel={`Remove search: ${q}`}
          />
        )}
        {dueToday && (
          <SummaryChip
            className={chip}
            icon={<CalendarCheck className='h-3 w-3' />}
            label='Due today'
            onRemove={() => setFilters({ dueToday: false })}
            ariaLabel='Remove due-today filter'
          />
        )}
        {pausedChip && (
          <SummaryChip
            className={chip}
            icon={<PauseCircle className='h-3 w-3' />}
            label='Showing paused'
            onRemove={() => setFilters({ includeArchived: false })}
            ariaLabel='Hide paused concepts'
          />
        )}
        {selectedLessons.map(id => (
          <SummaryChip
            className={chip}
            key={`lesson-${id}`}
            icon={<BookOpen className='h-3 w-3' />}
            label={lessonTitle(id)}
            onRemove={() =>
              setFilters({ lessons: selectedLessons.filter(x => x !== id) })
            }
            ariaLabel={`Remove lesson filter ${lessonTitle(id)}`}
          />
        ))}
        {selectedDomain.map(tag => (
          <SummaryChip
            className={chip}
            key={`domain-${tag}`}
            icon={<Tag className='h-3 w-3' />}
            label={tag}
            onRemove={() =>
              setFilters({ domain: selectedDomain.filter(x => x !== tag) })
            }
            ariaLabel={`Remove domain filter ${tag}`}
          />
        ))}
        {selectedUser.map(tag => (
          <SummaryChip
            className={chip}
            key={`user-${tag}`}
            icon={<Bookmark className='h-3 w-3' />}
            label={tag}
            onRemove={() =>
              setFilters({ user: selectedUser.filter(x => x !== tag) })
            }
            ariaLabel={`Remove your tag filter ${tag}`}
          />
        ))}
        {timeWindow !== 'all' && (
          <SummaryChip
            className={chip}
            icon={<Clock className='h-3 w-3' />}
            label={WINDOW_LABEL[timeWindow]}
            onRemove={() => setFilters({ window: 'all' })}
            ariaLabel={`Remove time window ${WINDOW_LABEL[timeWindow]}`}
          />
        )}
      </div>
    </div>
  );
}
